import type { SymbolKey } from '@/entities/symbol';

import { getFromToValues } from './getFromToValues';

import type { ExportSettingsType } from '../types/context.types';
import type { PreviewGenerationContext } from './PreviewService.types';

/**
 * Options for building a preview context
 */
export interface BuildPreviewContextOptions {
  /** Export settings containing date range configuration */
  exportSettings: ExportSettingsType;
  /** Selected symbols to export */
  symbols: SymbolKey[];
  /** User's timezone for date handling */
  timezone: string;
  /** User's API key (or placeholder) */
  apiKey: string;
  /** Base URL for GX API */
  gxURL: string;
  /** Optional override for range start (e.g., for Power Query which doesn't use date range) */
  rangeStartOverride?: string;
  /** Optional override for range end */
  rangeEndOverride?: string;
}

/**
 * Builds a consistent PreviewGenerationContext from export settings and other dependencies.
 * Use this helper instead of constructing the context inline in preview hooks.
 *
 * @example
 * ```ts
 * const context = buildPreviewContext({
 *   exportSettings,
 *   symbols,
 *   timezone: timezoneValue,
 *   apiKey,
 *   gxURL: GX_API_URL
 * });
 * ```
 */
export const buildPreviewContext = ({
  exportSettings,
  symbols,
  timezone,
  apiKey,
  gxURL,
  rangeStartOverride,
  rangeEndOverride
}: BuildPreviewContextOptions): PreviewGenerationContext => {
  const { fromValue, toValue } = getFromToValues(exportSettings);

  return {
    symbols,
    rangeStart: rangeStartOverride ?? fromValue ?? 'all',
    rangeEnd: rangeEndOverride ?? toValue ?? '',
    timezone,
    includeMetadata: exportSettings.includeMetadata,
    apiKey,
    gxURL
  };
};
